import { useState } from 'react';
import { GitBranch, Search, X } from 'lucide-react';
import { Badge, Button, Checkbox, Dialog, DialogContent, DialogFooter, Input } from '@vwt/ui';
import { useApiQuery } from '../lib/api.js';
import { useTranslation } from '../lib/i18n.js';

interface RepositoryRef {
  id: string;
  fullName: string;
}

/**
 * Chooses the repositories linked to a work item or attached to a project. Searches the normalized
 * GitHub repositories the tracker knows; saving replaces the whole set.
 */
export function RepositoryDialog({ title, description, initial, onSave, onClose }: { title: string; description?: string; initial: RepositoryRef[]; onSave: (repositoryIds: string[]) => Promise<boolean | void>; onClose: () => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<RepositoryRef[]>(initial);
  const [busy, setBusy] = useState(false);
  const results = useApiQuery('listRepositories', { query: { q: q.trim() || undefined, limit: '20' } });
  const isSelected = (id: string) => selected.some((r) => r.id === id);
  const toggle = (repo: RepositoryRef) => setSelected((current) => (current.some((r) => r.id === repo.id) ? current.filter((r) => r.id !== repo.id) : [...current, { id: repo.id, fullName: repo.fullName }]));
  const unchanged = selected.length === initial.length && selected.every((r) => initial.some((i) => i.id === r.id));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={title} description={description} closeLabel={t('common.close')}>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              // A save that reports failure (for example a conflict) keeps the dialog open.
              if ((await onSave(selected.map((r) => r.id))) !== false) onClose();
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="flex min-h-7 flex-wrap gap-1" aria-live="polite">
            {selected.length ? (
              selected.map((r) => (
                <Badge key={r.id} tone="neutral" className="gap-1">
                  <GitBranch className="size-3" /> {r.fullName}
                  <button type="button" onClick={() => toggle(r)} aria-label={t('repoPicker.remove', { name: r.fullName })} className="rounded hover:bg-muted">
                    <X className="size-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <span className="text-xs text-fg-subtle">{t('repoPicker.noneSelected')}</span>
            )}
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-fg-subtle" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('repoPicker.search')} aria-label={t('repoPicker.search')} className="pl-8" autoFocus />
          </div>
          <ul className="max-h-64 overflow-y-auto rounded-[var(--radius)] border border-border">
            {(results.data?.data ?? []).map((repo) => (
              <li key={repo.id}>
                <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-surface-2">
                  <Checkbox checked={isSelected(repo.id)} onCheckedChange={() => toggle(repo)} aria-label={repo.fullName} />
                  <span className="min-w-0 flex-1 truncate">{repo.fullName}</span>
                  {repo.archived ? <Badge tone="outline">{t('repoPicker.archived')}</Badge> : null}
                </label>
              </li>
            ))}
            {results.isSuccess && results.data.data.length === 0 ? <li className="px-3 py-4 text-center text-xs text-fg-subtle">{t('common.noResults')}</li> : null}
          </ul>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={busy} disabled={unchanged}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
